import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { GraduationCap, ArrowLeft, CheckCircle, AlertCircle, UserCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { goBack } from '../../utils/navigation';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const FORMACIONES = [
  'Lic. en Trabajo Social',
  'Tec. en Minoridad y Familia',
  'Trabajadore Social (nivel terciario)',
  'Estudiante Universitaria de la Carrera de Licenciatura en Trabajo Social',
  'Estudiante Universitaria de la Carrera de Tecnicatura en Minoridad y Familia',
  'Estudiante Terciario Carrera de Trabajo Social',
  'Otros'
];

// Formaciones que requieren matrícula
const FORMACIONES_CON_MATRICULA = [
  'Lic. en Trabajo Social',
  'Tec. en Minoridad y Familia',
  'Trabajadore Social (nivel terciario)'
];

export default function InscripcionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, trabajador } = useAuth();
  const [curso, setCurso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [datosPrecompletados, setDatosPrecompletados] = useState(false);
  
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    dni: '',
    telefono: '',
    formacion_academica: '',
    formacion_academica_otro: '',
    matricula: '',
    distrito: '',
    email: '',
    email_confirmacion: '',
    campo_extra: ''
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchCurso();
  }, [id]);

  // Precompletar datos si el usuario está autenticado
  useEffect(() => {
    if (isAuthenticated && trabajador && !datosPrecompletados) {
      // Mapear título a formación académica
      let formacion = '';
      if (trabajador.titulo) {
        if (trabajador.titulo.includes('Lic.') && trabajador.titulo.includes('Trabajo Social')) {
          formacion = 'Lic. en Trabajo Social';
        } else if (trabajador.titulo.includes('Tec.') || trabajador.titulo.includes('Minoridad')) {
          formacion = 'Tec. en Minoridad y Familia';
        } else if (trabajador.titulo.includes('Trabajador Social') || trabajador.titulo.includes('terciario')) {
          formacion = 'Trabajadore Social (nivel terciario)';
        }
      }
      
      setFormData(prev => ({
        ...prev,
        nombre: trabajador.nombre || '',
        apellido: trabajador.apellido || '',
        dni: trabajador.documento || '',
        telefono: trabajador.celular || '',
        email: trabajador.email || '',
        email_confirmacion: trabajador.email || '',
        matricula: trabajador.numero_matricula || '',
        distrito: 'Lomas de Zamora',
        formacion_academica: formacion
      }));
      setDatosPrecompletados(true);
    }
  }, [isAuthenticated, trabajador, datosPrecompletados]);

  const fetchCurso = async () => {
    try {
      const res = await axios.get(`${API_URL}/capacitaciones/cursos/${id}`);
      setCurso(res.data);
    } catch (err) {
      setError('Curso no encontrado');
    } finally {
      setLoading(false);
    }
  };

  // Determinar si la matrícula es obligatoria
  const matriculaObligatoria = FORMACIONES_CON_MATRICULA.includes(formData.formacion_academica);

  const validateForm = () => {
    const errors = {};
    
    if (!formData.nombre.trim()) errors.nombre = 'El nombre es obligatorio';
    if (!formData.apellido.trim()) errors.apellido = 'El apellido es obligatorio';
    if (!formData.dni.trim()) {
      errors.dni = 'El DNI es obligatorio';
    } else if (!/^\d{7,8}$/.test(formData.dni.replace(/[.\s-]/g, ''))) {
      errors.dni = 'El DNI ingresado no parece válido. Ingresá solo números, sin puntos ni espacios, entre 7 y 8 dígitos.';
    }
    if (!formData.telefono.trim()) errors.telefono = 'El teléfono es obligatorio';
    
    if (!formData.formacion_academica) {
      errors.formacion_academica = 'Seleccioná tu formación académica';
    }
    
    if (formData.formacion_academica === 'Otros' && !formData.formacion_academica_otro.trim()) {
      errors.formacion_academica = 'Especificá tu formación académica';
    }
    
    // Distrito obligatorio
    if (!formData.distrito.trim()) {
      errors.distrito = 'El distrito es obligatorio';
    }
    
    // Matrícula obligatoria solo para Profesional del Trabajo Social
    if (matriculaObligatoria && !formData.matricula.trim()) {
      errors.matricula = 'La matrícula es obligatoria para profesionales';
    }
    
    // Validar que matrícula sea solo números si se ingresó
    if (formData.matricula.trim() && !/^\d+$/.test(formData.matricula.trim())) {
      errors.matricula = 'La matrícula debe contener solo números';
    }
    
    if (!formData.email.trim()) errors.email = 'El email es obligatorio';
    if (!formData.email_confirmacion.trim()) errors.email_confirmacion = 'Confirmá tu email';
    
    if (formData.email && formData.email_confirmacion && formData.email !== formData.email_confirmacion) {
      errors.email_confirmacion = 'Los emails no coinciden';
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      errors.email = 'El formato del email es inválido';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSubmitting(true);
    setError(null);
    
    try {
      await axios.post(`${API_URL}/capacitaciones/cursos/${id}/inscribirse`, {
        nombre: formData.nombre,
        apellido: formData.apellido,
        dni: formData.dni.replace(/[.\s-]/g, ''),
        telefono: formData.telefono || null,
        matricula: formData.matricula || null,
        email: formData.email,
        distrito: formData.distrito || null,
        campo_extra: formData.campo_extra || null,
        formacion_academica: formData.formacion_academica || null,
        formacion_academica_otro: formData.formacion_academica === 'Otros'
          ? (formData.formacion_academica_otro || null)
          : null
      });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === 'Ya existe una inscripción con este DNI para este curso') {
        setError('Ya te inscribiste a este curso con este DNI');
      } else {
        setError(detail || 'Error al procesar la inscripción');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!curso) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <GraduationCap className="w-16 h-16 text-gray-300 mb-4" />
        <p className="text-gray-500 mb-4">Curso no encontrado</p>
        <button onClick={() => goBack()} className="text-emerald-600 hover:underline">
          ← Volver
        </button>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo-colegio.png" alt="Logo" className="h-12 w-auto" />
            </Link>
          </div>
        </header>
        
        <main className="max-w-lg mx-auto px-4 py-12">
          <Card>
            <CardContent className="pt-8 text-center">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Inscripción exitosa!</h2>
              <p className="text-gray-600 mb-6">
                Te inscribiste correctamente a <strong>{curso.nombre}</strong>. 
                Recibirás un email de confirmación en breve.
              </p>
              <div className="space-y-3">
                <Button variant="outline" className="w-full" onClick={() => goBack()}>
                  Volver
                </Button>
                <Link to="/capacitaciones">
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                    Ver más capacitaciones
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo-colegio.png" alt="Logo" className="h-12 w-auto" />
            </Link>
            <button
              onClick={() => goBack()}
              className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-lg mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-center">
              <GraduationCap className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
              Inscripción
            </CardTitle>
            <p className="text-center text-gray-600 text-sm">{curso.nombre}</p>
          </CardHeader>
          <CardContent>
            {/* Mensaje de datos precompletados */}
            {datosPrecompletados && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                <UserCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-emerald-700 text-sm font-medium">Datos precompletados</p>
                  <p className="text-emerald-600 text-sm">Completamos tus datos automáticamente. Verificalos antes de enviar.</p>
                </div>
              </div>
            )}
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Nombre y Apellido */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nombre">Nombre *</Label>
                  <Input
                    id="nombre"
                    value={formData.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                    className={formErrors.nombre ? 'border-red-500' : ''}
                    data-testid="input-nombre"
                  />
                  {formErrors.nombre && <p className="text-red-500 text-xs mt-1">{formErrors.nombre}</p>}
                </div>
                <div>
                  <Label htmlFor="apellido">Apellido *</Label>
                  <Input
                    id="apellido"
                    value={formData.apellido}
                    onChange={(e) => handleChange('apellido', e.target.value)}
                    className={formErrors.apellido ? 'border-red-500' : ''}
                    data-testid="input-apellido"
                  />
                  {formErrors.apellido && <p className="text-red-500 text-xs mt-1">{formErrors.apellido}</p>}
                </div>
              </div>

              {/* DNI y Teléfono */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="dni">DNI * (solo números)</Label>
                  <Input
                    id="dni"
                    value={formData.dni}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      handleChange('dni', value);
                    }}
                    placeholder="Ej: 12345678"
                    className={formErrors.dni ? 'border-red-500' : ''}
                    data-testid="input-dni"
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                  {formErrors.dni && <p className="text-red-500 text-xs mt-1">{formErrors.dni}</p>}
                </div>
                <div>
                  <Label htmlFor="telefono">Teléfono *</Label>
                  <Input
                    id="telefono"
                    value={formData.telefono}
                    onChange={(e) => handleChange('telefono', e.target.value)}
                    placeholder="Ej: 1155667788"
                    className={formErrors.telefono ? 'border-red-500' : ''}
                    data-testid="input-telefono"
                  />
                  {formErrors.telefono && <p className="text-red-500 text-xs mt-1">{formErrors.telefono}</p>}
                </div>
              </div>

              {/* Formación Académica */}
              <div>
                <Label>Formación Académica *</Label>
                <div className="space-y-2 mt-2">
                  {FORMACIONES.map((opcion) => (
                    <label key={opcion} className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="formacion_academica"
                        value={opcion}
                        checked={formData.formacion_academica === opcion}
                        onChange={(e) => handleChange('formacion_academica', e.target.value)}
                        className="w-4 h-4 text-emerald-600 mt-0.5"
                        data-testid={`radio-formacion-${opcion.toLowerCase().replace(/\s+/g, '-')}`}
                      />
                      <span className="text-sm text-gray-700">{opcion}</span>
                    </label>
                  ))}
                  {formData.formacion_academica === 'Otros' && (
                    <Input
                      placeholder="Especificar formación académica"
                      value={formData.formacion_academica_otro}
                      onChange={(e) => handleChange('formacion_academica_otro', e.target.value)}
                      className="mt-2 ml-6"
                      data-testid="input-formacion-otro"
                    />
                  )}
                </div>
                {formErrors.formacion_academica && <p className="text-red-500 text-xs mt-1">{formErrors.formacion_academica}</p>}
              </div>

              {/* Matrícula - Solo obligatoria para Profesional */}
              <div>
                <Label htmlFor="matricula">
                  Nº Matrícula {matriculaObligatoria ? '* (solo números)' : '(opcional, solo números)'}
                </Label>
                <Input
                  id="matricula"
                  value={formData.matricula}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    handleChange('matricula', value);
                  }}
                  className={formErrors.matricula ? 'border-red-500' : ''}
                  placeholder={matriculaObligatoria ? 'Ej: 1234' : 'Solo para profesionales matriculadxs'}
                  data-testid="input-matricula"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                {formErrors.matricula && <p className="text-red-500 text-xs mt-1">{formErrors.matricula}</p>}
              </div>

              {/* Distrito */}
              <div>
                <Label htmlFor="distrito">Distrito *</Label>
                <Input
                  id="distrito"
                  value={formData.distrito}
                  onChange={(e) => handleChange('distrito', e.target.value)}
                  placeholder="Ej: Lomas de Zamora"
                  className={formErrors.distrito ? 'border-red-500' : ''}
                  data-testid="input-distrito"
                />
                {formErrors.distrito && <p className="text-red-500 text-xs mt-1">{formErrors.distrito}</p>}
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className={formErrors.email ? 'border-red-500' : ''}
                  data-testid="input-email"
                />
                {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
              </div>

              {/* Confirmar Email */}
              <div>
                <Label htmlFor="email_confirmacion">Confirmar Email *</Label>
                <Input
                  id="email_confirmacion"
                  type="email"
                  value={formData.email_confirmacion}
                  onChange={(e) => handleChange('email_confirmacion', e.target.value)}
                  className={formErrors.email_confirmacion ? 'border-red-500' : ''}
                  data-testid="input-email-confirm"
                />
                {formErrors.email_confirmacion && <p className="text-red-500 text-xs mt-1">{formErrors.email_confirmacion}</p>}
              </div>

              {/* Campo extra del curso */}
              {curso.campo_extra_nombre && (
                <div>
                  <Label htmlFor="campo_extra">{curso.campo_extra_nombre} (opcional)</Label>
                  <Input
                    id="campo_extra"
                    value={formData.campo_extra}
                    onChange={(e) => handleChange('campo_extra', e.target.value)}
                    data-testid="input-campo-extra"
                  />
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={submitting}
                data-testid="btn-submit-inscripcion"
              >
                {submitting ? 'Procesando...' : 'Confirmar Inscripción'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
